import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import ForgotPassword from "./ForgotPassword";

// Minimal, production-ready rewrite
// - Redirects to /hrpay after successful login (or to state.from)
// - Keeps dark mode toggle, success/error popups, simple animations
// - Clean separation of concerns and accessibility tweaks

const LoginPage = () => {
  const { login } = useContext(AuthContext) || {};
  const { darkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();

  // Form state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFadeIn(true), 80);
    return () => clearTimeout(t);
  }, []);

  const showError = (msg) => {
    setErrorMessage(msg || "Something went wrong.");
    setSuccessMessage("");
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 3000);
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg || "Signed in successfully.");
    setErrorMessage("");
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 2500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return showError("Enter username and password.");
    if (!login) return showError("Auth service not available.");

    try {
      setLoading(true);
      await login(username.trim(), password);
      showSuccess("Signed in successfully.");

      // Redirect target: previous route (ProtectedRoute stored in state) or /hrpay
      const from = location.state?.from?.pathname || "/hrpay";
      navigate(from, { replace: true });
    } catch (err) {
      showError("Incorrect username or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = (message = "") => {
    setShowForgotPassword(false);
    if (message) showSuccess(message);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Logo */}
      <div
        className={`absolute top-4 left-4 transition-all duration-700 ease-out ${
          fadeIn ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
        }`}
      >
        <img src="/uasText.png" alt="UAS Logo" className="h-12 w-auto object-contain" />
      </div>

      {/* Dark mode toggle */}
      <div
        className={`absolute top-4 right-4 transition-all duration-700 ease-out ${
          fadeIn ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
        }`}
        style={{ zIndex: 10 }}
      >
        <button
          onClick={toggleDarkMode}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
            darkMode
              ? "bg-neutral-800 hover:bg-neutral-700 text-uasYellow"
              : "bg-neutral-200 hover:bg-neutral-300 text-neutral-800"
          }`}
        >
          {darkMode ? <MoonIcon /> : <SunIcon />}
        </button>
      </div>

      {/* Popup */}
      {showPopup && (
        <div
          className={`fixed top-16 right-4 z-50 px-4 py-3 rounded shadow-md transition-opacity duration-300 ${
            errorMessage
              ? "bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300"
              : "bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-300"
          }`}
          role="status"
          aria-live="polite"
        >
          <p>{errorMessage || successMessage}</p>
        </div>
      )}

      {/* Subtle background blobs */}
      <BackgroundBlobs fadeIn={fadeIn} />

      {/* Main content */}
      {showForgotPassword ? (
        <ForgotPassword onBackToLogin={handleBackToLogin} />
      ) : (
        <div
          className={`relative w-full max-w-md rounded-2xl shadow-lg p-8 border-t-8 bg-white/60 dark:bg-neutral-900/90 border-black dark:border-uasYellow transition-all duration-700 ease-out ${
            fadeIn ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
          }`}
        >
          <div className="mb-6 text-center">
            <img src="/uasAnimatedLogo.gif" alt="Company Logo" className="h-40 w-auto mx-auto" />
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">Sign in to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
            <TextInput
              label="Username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              autoComplete="username"
            />
            <TextInput
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black dark:bg-uasYellow text-white dark:text-black font-medium py-2.5 rounded-lg transition hover:opacity-90 disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Login"}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-neutral-600 dark:text-neutral-300 hover:underline"
              >
                Forgot Password?
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

/* ───────── Helpers ───────── */

const TextInput = ({ label, type = "text", value, onChange, autoFocus, autoComplete }) => (
  <label className="block">
    <span className="text-sm text-neutral-800 dark:text-neutral-200">{label}</span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      required
      className="mt-1 w-full border border-neutral-300 dark:border-neutral-700 rounded-lg px-3 py-2 bg-white/80 dark:bg-white/10 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-uasYellow outline-none"
    />
  </label>
);

const BackgroundBlobs = ({ fadeIn }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div
      className={`absolute -top-20 -left-20 w-64 h-64 bg-uasYellow/90 dark:bg-gray-600 rounded-full blur-3xl opacity-25 transition-all duration-[1200ms] ease-in-out ${
        fadeIn ? "translate-y-0" : "translate-y-full"
      }`}
    />
    <div
      className={`absolute top-1/2 -right-32 w-96 h-96 bg-black/80 dark:bg-uasYellow rounded-full blur-3xl opacity-10 transition-all duration-[1200ms] delay-200 ease-in-out ${
        fadeIn ? "translate-x-0" : "-translate-x-full"
      }`}
    />
    <div
      className={`absolute -bottom-20 left-1/3 w-80 h-80 bg-neutral-400/80 dark:bg-gray-500 rounded-full blur-3xl opacity-10 transition-all duration-[1200ms] delay-300 ease-in-out ${
        fadeIn ? "translate-y-0" : "translate-y-full"
      }`}
    />
  </div>
);

const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-[22px] w-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-[22px] w-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

export default LoginPage;
